<template>
  <div
    v-if="isVisible"
    class="!fixed !inset-0 !bg-black/50 !flex !items-center !justify-center !z-[1000] !p-4"
    @click="handleBackdropClick"
  >
    <div
      ref="dialogRef"
      class="!bg-white !rounded-xl !max-w-lg !w-full !max-h-[90vh] !overflow-y-auto !shadow-2xl !relative"
      role="dialog"
      aria-labelledby="permission-title"
      aria-describedby="permission-description"
      aria-modal="true"
      @click.stop
    >
      <div
        class="!flex !items-center !justify-between !p-6 !pb-4 !border-b !border-gray-200"
      >
        <h2
          id="permission-title"
          class="!text-2xl !font-semibold !text-gray-900 !flex !items-center !gap-2 !m-0"
        >
          <span class="!text-2xl">📍</span>
          Accès à votre position
        </h2>
        <button
          type="button"
          class="!p-2 !hover:bg-gray-100 !rounded-lg !transition-colors !border-0 !bg-transparent !cursor-pointer"
          @click="handleDeny"
          aria-label="Fermer la boite de dialogue"
        >
          <span class="!text-xl">✕</span>
        </button>
      </div>

      <div class="!p-6">
        <p
          id="permission-description"
          class="!text-gray-600 !leading-relaxed !mb-6"
        >
          {{ reason }}
        </p>

        <div class="!mb-6">
          <h3 class="!text-lg !font-semibold !text-gray-900 !mb-4">
            Pourquoi avons-nous besoin de votre position ?
          </h3>
          <ul class="!space-y-3 !list-none !p-0 !m-0">
            <li
              v-for="benefit in benefits"
              :key="benefit"
              class="!flex !items-start !gap-3"
            >
              <span class="!text-green-500 !text-xl !flex-shrink-0 !mt-1"
                >✓</span
              >
              <span class="!text-gray-600">{{ benefit }}</span>
            </li>
          </ul>
        </div>

        <div
          class="!p-4 !bg-blue-50 !rounded-lg !border !border-blue-200 !border-l-4 !border-l-blue-500"
        >
          <div class="!flex !items-start !gap-3">
            <span class="!text-blue-500 !text-xl !flex-shrink-0 !mt-0.5"
              >🛡️</span
            >
            <span class="!text-sm !text-gray-700 !leading-relaxed">
              Votre position est utilisée uniquement pour améliorer votre
              expérience et n'est pas partagée.
            </span>
          </div>
        </div>
      </div>

      <div
        class="!flex !gap-3 !p-6 !pt-0"
        role="group"
        aria-label="Choix de permission"
      >
        <button
          type="button"
          class="!flex-1 !flex !items-center !justify-center !gap-2 !px-6 !py-3 !bg-gray-100 !text-gray-700 !rounded-lg !font-medium !transition-colors !border !border-gray-300 !hover:bg-gray-200 !cursor-pointer"
          @click="handleDeny"
          ref="denyButtonRef"
        >
          <span>✗</span>
          Refuser
        </button>

        <button
          type="button"
          class="!flex-1 !flex !items-center !justify-center !gap-2 !px-6 !py-3 !bg-blue-600 !text-white !rounded-lg !font-medium !transition-colors !border-0 !hover:bg-blue-700 !cursor-pointer"
          @click="handleAllow"
          ref="allowButtonRef"
        >
          <span>📍</span>
          Autoriser
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import {
  checkGeolocationPermission,
  getCurrentPositionWithPermissionCheck,
} from '../utils/permissionUtils.js'

/**
 * Détermine si on est en mode Capacitor
 */
function isCapacitorMode() {
  return window.isCapacitorApp === true
}

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  reason: {
    type: String,
    default:
      "Nous avons besoin d'accéder à votre position pour vous proposer les options les plus proches de vous.",
  },
  benefits: {
    type: Array,
    default: () => [
      'Trouver les services les plus proches',
      'Calculer les itinéraires les plus rapides',
      'Personnaliser votre expérience',
    ],
  },
})

const emit = defineEmits(['allow', 'deny', 'error'])

const dialogRef = ref(null)
const denyButtonRef = ref(null)
const allowButtonRef = ref(null)
const focusableElements = ref([])
let previousFocusElement = null

// Gestion du focus trap
const trapFocus = () => {
  if (!dialogRef.value) return

  const selector =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  focusableElements.value = Array.from(
    dialogRef.value.querySelectorAll(selector),
  )

  if (focusableElements.value.length > 0) {
    focusableElements.value[0].focus()
  }
}

const handleTabNavigation = e => {
  if (e.key !== 'Tab') return

  const firstElement = focusableElements.value[0]
  const lastElement =
    focusableElements.value[focusableElements.value.length - 1]

  if (e.shiftKey) {
    if (document.activeElement === firstElement) {
      e.preventDefault()
      lastElement.focus()
    }
  } else {
    if (document.activeElement === lastElement) {
      e.preventDefault()
      firstElement.focus()
    }
  }
}

const handleKeydown = e => {
  switch (e.key) {
    case 'Escape':
      handleDeny()
      break
    case 'Tab':
      handleTabNavigation(e)
      break
  }
}

const handleBackdropClick = () => {
  handleDeny()
}

const handleAllow = async () => {
  try {
    // Demander la permission de localisation
    const position = await getCurrentPositionWithPermissionCheck()
    emit('allow', position)
  } catch (error) {
    emit('error', error)
  }
}

const handleDeny = () => {
  emit('deny')
}

// Gestion du focus avant et après la modale
const saveFocusElement = () => {
  previousFocusElement = document.activeElement
}

const restoreFocus = () => {
  if (previousFocusElement && previousFocusElement.focus) {
    previousFocusElement.focus()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Watcher pour la visibilité de la modale
const setupDialog = async () => {
  if (props.isVisible) {
    // En mode Capacitor, on autorise directement sans afficher le dialogue
    if (isCapacitorMode()) {
      try {
        const position = await getCurrentPositionWithPermissionCheck()
        emit('allow', position)
      } catch (error) {
        emit('error', error)
      }
      return
    }

    saveFocusElement()
    await nextTick()
    trapFocus()
  } else {
    restoreFocus()
  }
}

// Réagir aux changements de visibilité
import { watch } from 'vue'
watch(() => props.isVisible, setupDialog, { immediate: true })
</script>
